import type { Board } from "../board";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import { PRIORITY_DEFAULT, type ProcessableThing, isRemoteDoor } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { Player } from "../player";
import { deserializeXY } from "../serialization";
import { Strings } from "../strings/strings";
import type { XY } from "../xy";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { Terminal, TerminalState } from "./terminal";

const PROMPT_RADIUS = 1;

export class OpenBlastDoorsTerminal extends Terminal implements ProcessableThing {
	static key = "OpenBlastDoorsTerminal";
	readonly doorId: string | null;
	readonly requiredItemKey: string | null;

	constructor(xy: XY, doorId?: string, requiredItemKey?: string) {
		super(xy);
		this.doorId = doorId ?? null;
		this.requiredItemKey = requiredItemKey ?? null;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const terminal = new OpenBlastDoorsTerminal(
			xy,
			props?.group as string | undefined,
			props?.requiredItem as string | undefined,
		);
		board.putThing(terminal);
		return [terminal];
	}

	override interact(player: PhysicalThing): void {
		if (this.state === TerminalState.ACTIVE && this.requiredItemKey !== null) {
			if (!(player instanceof Player) || !player.inventory.consumeOneByKey(this.requiredItemKey)) {
				const board = this.board!;
				board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR, this.xy));
				board.addEvent(new Event(EventType.TERMINAL_NO_KEYCARD));
				return;
			}
		}
		super.interact(player);
	}

	processTurn(board: Board): void {
		if (!this.canInteract()) return;
		const player = board.player;
		if (!player) return;
		if (!this.xy.isInSquareRadius(player.xy, PROMPT_RADIUS)) return;
		const text = Strings.get("prompt_useTerminal") ?? "prompt_useTerminal";
		board.emitPrompt(text);
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	runTerminalAction(): void {
		const board = this.board!;
		for (const thing of board.getPhysicalThings()) {
			if (isRemoteDoor(thing)) {
				if (this.doorId === null || thing.doorId === this.doorId) {
					thing.open();
				}
			}
		}
		this.board!.addNonPhysicalThing(new Sound(SoundId.TERMINAL_CONFIRM, this.xy));
		this.board!.addEvent(new Event(EventType.TERMINAL_OPENED_BLAST_DOORS));
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			state: this.state,
			doorId: this.doorId,
			requiredItemKey: this.requiredItemKey,
		};
	}

	static deserialize(data: any): OpenBlastDoorsTerminal {
		const t = new OpenBlastDoorsTerminal(deserializeXY(data.xy), data.doorId ?? undefined, data.requiredItemKey ?? undefined);
		t.visualId = data.visualId;
		t.state = data.state ?? TerminalState.ACTIVE;
		return t;
	}
}
